import {useChatListContext} from "../providers/ChatListProvider";
import {MessageComposer} from "../components/chat/MessageComposer";
import {Ping, WorkspaceMember} from "../lib/types";
import {v4 as uuidv4} from "uuid";
import {extractMentions, PreviewRenderer, slateToPlainText} from "../components/chat/PreviewRenderer";
import {useUserContext} from "../providers/UserContext";
import React, {useCallback, useEffect, useState} from "react";
import {ReactEditor} from "slate-react";
import {addPing, react, reactPing, removePingReaction, removeReaction, viewPings} from "../lib/database";
import {Icon} from "../components/common/Icon";
import {UserName} from "../components/common/UserName";
import {get} from "firebase/database";
import {Message} from "../components/chat/Message";
import {Descendant} from "slate";
import Picker from "@emoji-mart/react";
import data, {EmojiMartData} from "@emoji-mart/data";
import {User} from "firebase/auth";
import classNames from "classnames";
import {useLayoutContext} from "../providers/LayoutProvider";

export const Pings = ({}: {}) => {
  const emojis = data as EmojiMartData;

  const {setChatListVisible} = useChatListContext();
  const {isMobileResolution, isSidebarOpen, setSidebarVisibility} = useLayoutContext();
  const { user, pings, getMember } = useUserContext();
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [activePing, setActivePing] = useState<Ping | null>(null);
  const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
  const handleEmojiSelect = useCallback((emoji: any) => {
    if (activePing && user) {
      reactPing(activePing, user, emoji.id);
    }
    setShowEmojiPicker(false); // Hide the picker after selection
    setActivePing(null);
  },[user, activePing])
  setChatListVisible(false);
  useEffect(() => {
    if(user) {
      window.setTimeout(() => {
        viewPings(user);
      }, 2000);
    }
  }, [user, pings]);
  const createPing = useCallback((editor: ReactEditor) => {
    if (!user) {
      return;
    }
    const ping: Ping = {
      id: uuidv4(),
      content: JSON.stringify(editor.children),
      timestamp: Date.now(),
      authorId: user?.uid || "",
      authorName: user?.displayName || "",
      mentions: extractMentions(editor.children),
      groupMentions: [],
      reactions: {},
      lastAction: Date.now()
    }
    addPing(ping, user);
  }, [user]);
  return (
    <div className={"p-4 flex justify-center"}>
      <div className={"w-full max-w-[700px]"}>
        <div>
          <h1 className={
            classNames(
              "flex items-center gap-2",
              isMobileResolution ? "font-style-headline-2" : "font-style-headline-1"
            )
          }>
            {isMobileResolution && (
              <button onClick={() => setSidebarVisibility(!isSidebarOpen)} className={"flex items-center"}>
                <Icon name={isSidebarOpen ? "close" : "menu"} size={"medium"}/>
              </button>
            )}
            Pings
          </h1>
          <p className="font-style-body-small text-gray-3 mb-4">
            Short notices that don't require a full conversation
          </p>
        </div>
        <div>
          <MessageComposer onSend={(editor) => {
            createPing(editor);
          }}/>
        </div>
        <div className="mt-2 flex flex-col gap-4 mt-4">
          {showEmojiPicker && (
            <div>
              <div
                className="backdrop fixed z-40 left-0 top-0 w-full h-full"
                onClick={() => {
                  setShowEmojiPicker(false);
                }}
              ></div>
              <div
                className="fixed z-50"
                style={{
                  top: Math.min(pickerPosition.top - 50, window.innerHeight - 500),
                  left: pickerPosition.left - 380,
                }}
              >
                <Picker data={data} onEmojiSelect={handleEmojiSelect} />
              </div>
            </div>
          )}
          {pings.map(ping => (
            <div className={"ping p-4 rounded-lg border border-gray-6"}>
              <div className={"flex gap-2"}>
                <UserName user={getMember(ping.authorId) as WorkspaceMember} />
                <Icon name={"arrow_right_alt"} />
                <div className="flex gap-2">
                  {ping.mentions.map(((userId, index) => {
                   return (
                     <>
                       {index > 0 ? <span className={"-ml-2"}>, </span> : ""}
                        <UserName user={getMember(userId) as WorkspaceMember} />
                     </>
                  )}))}
                </div>

              </div>
              <div className={"mt-2"}>
                <PreviewRenderer content={JSON.parse(ping.content) as Descendant[]} />

                <div className="reactions flex gap-1 mt-2">
                  {ping.reactions &&
                    Object.keys(ping.reactions).map((reaction) => {
                      const didIReact = Object.keys(ping.reactions[reaction]).includes(user?.uid as string)
                      return (
                      <button
                        key={reaction}
                        onClick={() => {
                          if (didIReact) {
                            removePingReaction(ping, user as User, reaction);
                          } else {
                            reactPing(ping, user as User, reaction);
                          }
                        }}
                        className={classNames(
                          "reaction py-1 group/emoji relative hover:bg-gray-6 w-10 rounded-lg h-[25px] flex justify-center items-center font-style-label-xs text-blue-1",
                          didIReact ? "border border-blue-3 bg-gray-9" : "bg-gray-8"
                          )}
                      >
                        {emojis.emojis[reaction].skins[0].native}
                        <span className="ml-1">
                          {ping?.reactions &&
                            Object.keys(ping.reactions[reaction]).length}
                        </span>
                        <span className="hidden group-hover/emoji:flex hover:bg-gray-6 absolute w-[100px] shadow-elevation-1 flex-col justify-start text-left top-[100%] left-0 bg-gray-9 z-20 p-2 font-style-label-xxs rounded-[0_4px_4px_4px] -mt-1 text-gray-1">
                          {ping?.reactions &&
                            Object.keys(ping.reactions[reaction]).map(
                              (userId) => (
                                <span key={userId}>
                                  {getMember(userId)?.displayName}
                                </span>
                              )
                            )}
                        </span>
                      </button>
                    )})}
                  <div className="reaction bg-gray-9 hover:bg-gray-6 w-10 rounded-lg h-[25px] flex justify-center items-center">
                    <button
                      onClick={(e) => {
                        // Positionate to the right of the button
                        setActivePing(ping);
                        setPickerPosition({top: e.clientY, left: e.clientX});
                        setShowEmojiPicker(true);
                      }}
                    >
                      <Icon name={"add_reaction"} size={"xsmall"}/>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}